import React, { useState, useEffect } from 'react';
import { processingApi, ProcessingStats, ProcessingResult } from '../api/clients';
import { useAuth } from '../context/AuthContext';

const ProcessingStatus: React.FC = () => {
  const { apiRequest } = useAuth();
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Automated processor state
  const [isAutomatedEnabled, setIsAutomatedEnabled] = useState<boolean>(false);
  const [automatedStatus, setAutomatedStatus] = useState<string>('');
  const [automatedRunning, setAutomatedRunning] = useState<boolean>(false);
  const [automatedStats, setAutomatedStats] = useState<{
    pendingClaims: number;
    lastRun?: {
      time: string;
      results?: any;
    };
    nextRun?: string;
    intervalMinutes: number;
  } | null>(null);
  
  // Manual processing state
  const [batchSize, setBatchSize] = useState<number>(100);
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<ProcessingResult | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [processingAll, setProcessingAll] = useState<boolean>(false);
  const [totalProcessedInBatch, setTotalProcessedInBatch] = useState<number>(0);
  const [emailInput, setEmailInput] = useState<string>('');
  const [emailProcessing, setEmailProcessing] = useState<boolean>(false);
  const [emailResult, setEmailResult] = useState<{
    success: boolean;
    email: string;
    total_claims: number;
    successful: number;
    failed: number;
    client_id?: string | null;
    client_name?: string | null;
    message?: string;
  } | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(false);

  useEffect(() => {
    fetchStats();
    fetchAutomatedProcessorStatus();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await processingApi.getProcessingStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch processing stats');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomatedProcessorStatus = async () => {
    try {
      const response = await apiRequest<any>({
        url: '/ui/admin/processors/recent-claims/status',
        method: 'GET'
      });
      setIsAutomatedEnabled(response.enabled);
      setAutomatedStats({
        pendingClaims: response.pendingClaims || 0,
        lastRun: response.lastRun,
        nextRun: response.nextRun,
        intervalMinutes: response.intervalMinutes || 30
      });
    } catch (error) {
      console.error('Error fetching automated processor status:', error);
      setAutomatedStatus('Failed to fetch automated processor status');
    }
  };

  const toggleAutomatedProcessor = async (enable: boolean) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const response = await apiRequest<{success: boolean; message: string}>({
        url: `/ui/admin/processors/recent-claims/${endpoint}`,
        method: 'POST'
      });
      setIsAutomatedEnabled(enable);
      setAutomatedStatus(response.message || `Automated processor ${enable ? 'enabled' : 'disabled'} successfully`);
      fetchAutomatedProcessorStatus();
      
      // Enable auto-refresh when processor is enabled
      if (enable) {
        setAutoRefreshEnabled(true);
      }
    } catch (error) {
      console.error(`Error ${enable ? 'enabling' : 'disabling'} automated processor:`, error);
      setAutomatedStatus(`Failed to ${enable ? 'enable' : 'disable'} automated processor`);
    }
  };

  const handleRunAutomatedProcessor = async () => {
    try {
      setAutomatedRunning(true);
      setAutomatedStatus('Starting automated processor...');
      
      const response = await apiRequest<{
        success: boolean;
        message: string;
        lastRun?: any;
      }>({
        url: '/ui/admin/processors/recent-claims/run',
        method: 'POST'
      });
      
      if (response.success) {
        setAutomatedStatus(response.message);
        fetchAutomatedProcessorStatus(); // Refresh stats
        fetchStats(); // Refresh main stats too
      } else {
        setAutomatedStatus(response.message || 'Failed to run automated processor');
      }
    } catch (error) {
      console.error('Error running automated processor:', error);
      setAutomatedStatus('Failed to run automated processor');
    } finally {
      setAutomatedRunning(false);
    }
  };

  const handleProcess = async () => {
    try {
      setProcessing(true);
      setError(null);
      setLastResult(null);
      
      console.log('Processing with params:', { batchSize, dryRun });
      
      const result = await processingApi.processClaims(batchSize, dryRun);
      setLastResult(result);
      
      // Refresh stats after processing
      await fetchStats();
      
      if (result.failed > 0 && result.errors.length > 0) {
        console.error('Processing errors:', result.errors);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to process claims';
      setError(errorMessage);
      console.error('Error processing claims:', err);
      console.error('Error response:', err.response?.data);
    } finally {
      setProcessing(false);
    }
  };

  const getProgressBarWidth = () => {
    if (!stats || stats.total_claims === 0) return '0%';
    return `${Math.round(stats.processing_percentage)}%`;
  };

  // Auto-refresh effect for automated processor
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (autoRefreshEnabled && isAutomatedEnabled) {
      // Refresh every minute
      interval = setInterval(() => {
        fetchAutomatedProcessorStatus();
        fetchStats();
      }, 60000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefreshEnabled, isAutomatedEnabled]);

  const handleProcessAll = async () => {
    try {
      setProcessingAll(true);
      setProcessing(true);
      setError(null);
      setTotalProcessedInBatch(0);
      
      let totalProcessed = 0;
      let hasMore = true;
      
      while (hasMore && stats && stats.pending_claims > 0) {
        console.log(`Processing batch: ${totalProcessed} processed so far, ${stats.pending_claims} remaining`);
        
        // Process up to 1000 at a time
        const currentBatchSize = Math.min(1000, stats.pending_claims);
        
        try {
          const result = await processingApi.processClaims(currentBatchSize, false);
          totalProcessed += result.total_processed;
          setTotalProcessedInBatch(totalProcessed);
          setLastResult(result);
          
          // Refresh stats to get updated counts
          await fetchStats();
          
          // Check if we should continue
          if (result.total_processed === 0 || result.failed === result.total_processed) {
            hasMore = false;
            console.log('Stopping: No more claims to process or all failed');
          }
          
          // Add a small delay between batches to avoid overwhelming the server
          if (hasMore && stats.pending_claims > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (err) {
          console.error('Error in batch processing:', err);
          throw err;
        }
      }
      
      console.log(`Process All completed. Total processed: ${totalProcessed}`);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to process all claims';
      setError(errorMessage);
      console.error('Error processing all claims:', err);
    } finally {
      setProcessing(false);
      setProcessingAll(false);
      setTotalProcessedInBatch(0);
    }
  };

  const handleProcessByEmail = async () => {
    if (!emailInput.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setEmailProcessing(true);
      setError(null);
      setEmailResult(null);
      
      console.log('Processing claims for email:', emailInput);
      
      const result = await processingApi.processClaimsByEmail(emailInput.trim());
      setEmailResult(result);
      
      if (result.success) {
        // Refresh stats
        await fetchStats();
        
        // Show success message with client ID
        if (result.client_id) {
          console.log('Successfully processed. Client ID:', result.client_id);
        }
      } else {
        setError(result.message || 'No claims found for this email');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to process claims by email';
      setError(errorMessage);
      console.error('Error processing by email:', err);
    } finally {
      setEmailProcessing(false);
    }
  };

  return (
    <div className="processor-section">
      <h3>Automated Claims Processor</h3>
      <p className="description">
        Processes all unprocessed claims to extract and normalize client data. 
        Can run automatically on a schedule or be triggered manually.
      </p>

      {error && (
        <div className="status-message" style={{ 
          backgroundColor: '#f8d7da', 
          borderLeft: '4px solid #dc3545',
          color: '#721c24' 
        }}>
          {error}
        </div>
      )}

      {loading && !stats ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Loading stats...
        </div>
      ) : stats ? (
        <>
          <div className="loc-preview">
            <h4>Overall Progress</h4>
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Processing Status</span>
                <span style={{ fontWeight: 'bold' }}>{Math.round(stats.processing_percentage)}%</span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '20px', 
                backgroundColor: '#e9ecef', 
                borderRadius: '10px',
                overflow: 'hidden'
              }}>
                <div 
                  style={{ 
                    width: getProgressBarWidth(),
                    height: '100%',
                    backgroundColor: '#28a745',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '5px' }}>
                {stats.processed_claims} of {stats.total_claims} claims processed
              </div>
            </div>

            <ul>
              <li><strong>{stats.unique_clients}</strong> unique clients identified</li>
              <li><strong>{stats.normalized_claims}</strong> claims normalized</li>
              <li><strong>{stats.pending_claims}</strong> claims pending processing</li>
              <li><strong>{stats.failed_claims}</strong> claims failed</li>
            </ul>
          </div>

          {/* Automated Processing Status */}
          {automatedStats && (
            <div className="loc-preview" style={{ marginTop: '15px' }}>
              <h4>Automated Processing Status</h4>
              <ul>
                <li>Status: <strong>{isAutomatedEnabled ? 'Enabled' : 'Disabled'}</strong></li>
                <li>Processes all <strong>{automatedStats.pendingClaims}</strong> unprocessed claims</li>
                <li>Runs every <strong>{automatedStats.intervalMinutes} minutes</strong></li>
                {automatedStats.lastRun?.time && (
                  <>
                    <li>Last run: <strong>{new Date(automatedStats.lastRun.time).toLocaleString()}</strong></li>
                    {automatedStats.lastRun.results && (
                      <li>Last run processed: <strong>{automatedStats.lastRun.results.total_processed || 0}</strong> claims</li>
                    )}
                  </>
                )}
                {automatedStats.nextRun && (
                  <li>Next scheduled run: <strong>{new Date(automatedStats.nextRun).toLocaleString()}</strong></li>
                )}
              </ul>
            </div>
          )}

          {/* Automated Processing Controls */}
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ fontSize: '16px', marginBottom: '10px', color: '#495057' }}>Automated Processing Controls</h4>
            <div className="button-group">
              <button
                className="enable-btn"
                onClick={() => handleRunAutomatedProcessor()}
                disabled={automatedRunning}
              >
                {automatedRunning ? 'Processing...' : 'Run Now'}
              </button>
              <button
                className="enable-btn"
                onClick={() => toggleAutomatedProcessor(true)}
                disabled={isAutomatedEnabled}
              >
                Enable Automated Processing
              </button>
              <button
                className="disable-btn"
                onClick={() => toggleAutomatedProcessor(false)}
                disabled={!isAutomatedEnabled}
              >
                Disable Automated Processing
              </button>
              <button
                className="export-btn"
                onClick={() => {
                  fetchAutomatedProcessorStatus();
                  fetchStats();
                }}
                disabled={loading}
                style={{ backgroundColor: '#17a2b8' }}
              >
                Refresh Status
              </button>
            </div>
            
            {autoRefreshEnabled && isAutomatedEnabled && (
              <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '10px' }}>
                ✓ Auto-refresh enabled (updates every minute)
              </p>
            )}
            
            {automatedStatus && (
              <div className="status-message" style={{ 
                backgroundColor: automatedStatus.includes('Failed') ? '#f8d7da' : '#d4edda',
                borderLeft: automatedStatus.includes('Failed') ? '4px solid #dc3545' : '4px solid #28a745',
                marginTop: '15px'
              }}>
                {automatedStatus}
              </div>
            )}
          </div>

          <div style={{ marginTop: '20px' }}>
            <h4 style={{ fontSize: '16px', marginBottom: '10px', color: '#495057' }}>Process Single Client by Email</h4>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="email"
                placeholder="Enter client email address"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                disabled={emailProcessing}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  width: '300px',
                  marginRight: '10px'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !emailProcessing) {
                    handleProcessByEmail();
                  }
                }}
              />
              <button
                className="enable-btn"
                onClick={handleProcessByEmail}
                disabled={emailProcessing || !emailInput.trim()}
              >
                {emailProcessing ? 'Processing...' : 'Process Client'}
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#6c757d', margin: '5px 0' }}>
              This will find and process all claims for the specified email address
            </p>
          </div>

          {emailResult && (
            <div className="status-message" style={{ 
              backgroundColor: emailResult.success ? '#d4edda' : '#f8d7da',
              borderLeft: emailResult.success ? '4px solid #28a745' : '4px solid #dc3545',
              marginTop: '15px'
            }}>
              <strong>Email Processing Result</strong>
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                <span>
                  <strong>Email:</strong> {emailResult.email}
                </span>
                <span>
                  <strong>Claims Found:</strong> {emailResult.total_claims}
                </span>
                {emailResult.success && (
                  <>
                    <span>
                      <strong>Processed:</strong> {emailResult.successful}
                    </span>
                    {emailResult.client_id && (
                      <>
                        <span style={{ flex: '1 1 100%', marginTop: '10px' }}>
                          <strong>Client ID:</strong> <code style={{ 
                            backgroundColor: '#f3f4f6', 
                            padding: '2px 6px', 
                            borderRadius: '3px',
                            fontFamily: 'monospace'
                          }}>{emailResult.client_id}</code>
                        </span>
                        {emailResult.client_name && (
                          <span style={{ flex: '1 1 100%' }}>
                            <strong>Client Name:</strong> {emailResult.client_name}
                          </span>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              {emailResult.success && emailResult.client_id && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                  <strong>Next Step:</strong> You can now test ID verification for this client using the "Request ID" button in the Claim Management page
                </div>
              )}
            </div>
          )}

        </>
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No processing statistics available
        </div>
      )}

      <style>{`
        .processor-section {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 5px;
        }
        
        .processor-section .description {
          color: #666;
          font-style: italic;
          font-size: 14px;
          margin: 5px 0 15px 0;
        }
        
        .loc-preview {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 15px;
          margin: 15px 0;
        }
        
        .loc-preview h4 {
          margin: 0 0 10px 0;
          color: #495057;
          font-size: 16px;
        }
        
        .loc-preview ul {
          margin: 0;
          padding-left: 20px;
        }
        
        .loc-preview li {
          margin: 5px 0;
          color: #6c757d;
        }
        
        .button-group {
          display: flex;
          gap: 10px;
          margin: 15px 0;
        }
        
        .enable-btn, .disable-btn, .export-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          transition: background-color 0.2s, opacity 0.2s;
        }
        
        .enable-btn {
          background-color: #28a745;
          color: white;
        }
        
        .disable-btn {
          background-color: #dc3545;
          color: white;
        }
        
        .export-btn {
          background-color: #007bff;
          color: white;
        }
        
        .enable-btn:disabled, .disable-btn:disabled, .export-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        
        .status-message {
          margin-top: 15px;
          padding: 10px;
          border-radius: 4px;
          background-color: #f8f9fa;
          border-left: 4px solid #007bff;
        }
        
        .form-group {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
};

export default ProcessingStatus;